import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Target, Edit2, Trash2, X, CheckCircle, Clock, Send, Users2 } from "lucide-react";
import styles from "../hrms.module.scss";
import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });

const PRIORITIES = ["High","Medium","Low"];
const CATEGORIES = ["Quality","Learning","Delivery","Communication","Leadership","Initiative","Other"];
const STATUSES   = ["Not Started","In Progress","Submitted","Completed"];

export default function GoalsKPIs() {
  const { T } = useTheme();
  const P_COLOR = { High:T.red, Medium:T.amber, Low:T.green };
  const S_COLOR = {
    "Not Started":T.muted,"In Progress":T.cyan,
    "Submitted":T.amber,"Completed":T.green,"Rejected":T.red,
  };
  const inp = (err=false) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err?T.red:T.border}`,
    borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px",
    outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });
  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.07em" };

  const [goals,     setGoals]     = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState("all");
  const [toast,     setToast]     = useState("");

  const [open,   setOpen]   = useState(false);
  const [editId, setEditId] = useState(null);
  const [form,   setForm]   = useState({ title:"", description:"", kpi_metric:"", category:"Quality", priority:"Medium", assigned_to_id:"", due_date:"", start_date:"", progress:0 });

  const [reviewOpen,  setReviewOpen]  = useState(false);
  const [reviewGoal,  setReviewGoal]  = useState(null);
  const [reviewData,  setReviewData]  = useState({ approved:true, manager_note:"" });

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3500); };

  const load = async () => {
    setLoading(true);
    try {
      const [gRes, eRes] = await Promise.all([
        axios.get(`${API}/performance/goals`, ax()),
        axios.get(`${API}/employees/`,        ax()),
      ]);
      setGoals(gRes.data  || []);
      setEmployees(eRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return;
    try {
      const payload = { ...form, assigned_to_id: form.assigned_to_id ? parseInt(form.assigned_to_id) : null };
      if (editId) await axios.put(`${API}/performance/goals/${editId}`, payload, ax());
      else        await axios.post(`${API}/performance/goals`, payload, ax());
      setOpen(false); setEditId(null); load();
      flash(`✅ Goal ${editId?"updated":"created"}`);
    } catch(e) { flash("❌ " + (e.response?.data?.detail||"Error")); }
  };

  const del = async (id) => {
    try { await axios.delete(`${API}/performance/goals/${id}`, ax()); load(); flash("✅ Goal deleted"); }
    catch(e) { flash("❌ Error"); }
  };

  const submitReview = async () => {
    try {
      const userData   = JSON.parse(sessionStorage.getItem("platform_user")||"{}");
      await axios.put(`${API}/performance/goals/${reviewGoal.id}/review?platform_user_id=${userData.id}`, reviewData, ax());
      flash(`✅ Goal ${reviewData.approved?"Approved":"Sent back for revision"}`);
      setReviewOpen(false); load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail||"Error")); }
  };

  const openEdit = (g) => {
    setForm({ title:g.title, description:g.description||"", kpi_metric:g.kpi_metric||"",
      category:g.category, priority:g.priority, assigned_to_id:g.assigned_to_id||"",
      due_date:g.due_date||"", start_date:g.start_date||"", progress:g.progress });
    setEditId(g.id); setOpen(true);
  };

  const filtered = filter==="all" ? goals : goals.filter(g=>g.status===filter);

  const stats = {
    total:     goals.length,
    completed: goals.filter(g=>g.status==="Completed").length,
    inProgress:goals.filter(g=>g.status==="In Progress").length,
    submitted: goals.filter(g=>g.status==="Submitted").length,
    rate:      goals.length ? Math.round(goals.filter(g=>g.status==="Completed").length/goals.length*100) : 0,
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>PERFORMANCE</div>
          <h2>Goals & KPIs</h2>
          <p>Set, track and review employee goals across your company</p>
        </div>
        <button className={styles.btnPrimary}
          onClick={()=>{ setForm({ title:"", description:"", kpi_metric:"", category:"Quality", priority:"Medium", assigned_to_id:"", due_date:"", start_date:"", progress:0 }); setEditId(null); setOpen(true); }}>
          <Plus size={14}/> New Goal
        </button>
      </div>

      {/* Stats */}
      <div className={`${styles.statsGrid} ${styles.cols4}`} style={{ marginBottom:20 }}>
        {[
          { label:"Total Goals",      val:stats.total,      color:T.cyan },
          { label:"Completed",        val:stats.completed,  color:T.green },
          { label:"In Progress",      val:stats.inProgress, color:T.amber },
          { label:"Completion Rate",  val:`${stats.rate}%`, color:T.purple },
        ].map(({label,val,color}) => (
          <div key={label} className={styles.statCard} style={{ borderTop:`3px solid ${color}` }}>
            <div className={styles.statVal} style={{ color }}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Pending review banner */}
      {stats.submitted > 0 && (
        <div style={{ background:`${T.amber}0F`, border:`1px solid ${T.amber}33`, borderRadius:10, padding:"12px 18px", marginBottom:16, display:"flex", gap:10, alignItems:"center" }}>
          <Clock size={14} style={{ color:T.amber, flexShrink:0 }}/>
          <span style={{ fontSize:13, color:T.text }}>
            <strong style={{ color:T.amber }}>{stats.submitted}</strong> goal{stats.submitted>1?"s":""} submitted by employees — awaiting your review
          </span>
          <button onClick={()=>setFilter("Submitted")} style={{ marginLeft:"auto", fontSize:12, color:T.amber, background:"none", border:`1px solid ${T.amber}4D`, borderRadius:7, padding:"4px 10px", cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
            Review now →
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {["all",...STATUSES].map(s => (
          <button key={s} onClick={()=>setFilter(s)}
            style={{ padding:"6px 14px", borderRadius:20, fontSize:12, fontFamily:"Outfit,sans-serif", cursor:"pointer",
              border:`1px solid ${filter===s?`${T.cyan}4D`:T.toggle_bg}`,
              background:filter===s?`${T.cyan}14`:T.surface_faint,
              color:filter===s?T.cyan:T.muted }}>
            {s==="all"?"All":s}
            {s!=="all" && <span style={{ marginLeft:5, fontFamily:"JetBrains Mono,monospace", fontSize:10 }}>{goals.filter(g=>g.status===s).length}</span>}
          </button>
        ))}
      </div>

      {/* Goals grid */}
      {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))", gap:14 }}>
          {filtered.map(g => (
            <div key={g.id} style={{ background:T.card, border:`1px solid ${g.status==="Submitted"?`${T.amber}40`:T.border}`, borderRadius:12, padding:18, position:"relative" }}>
              {/* Status badge */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, fontWeight:600,
                  background:`${S_COLOR[g.status]||T.muted}15`,
                  color:S_COLOR[g.status]||T.muted }}>
                  {g.status}
                </span>
                <div style={{ display:"flex", gap:4 }}>
                  {g.status==="Submitted" && (
                    <button onClick={()=>{ setReviewGoal(g); setReviewData({approved:true,manager_note:""}); setReviewOpen(true); }}
                      style={{ fontSize:11, padding:"3px 8px", borderRadius:7, border:`1px solid ${T.green}4D`, background:`${T.green}14`, color:T.green, cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                      Review
                    </button>
                  )}
                  <button onClick={()=>openEdit(g)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted, padding:3 }}><Edit2 size={12}/></button>
                  <button onClick={()=>del(g.id)} style={{ background:"none", border:"none", cursor:"pointer", color:T.red, padding:3 }}><Trash2 size={12}/></button>
                </div>
              </div>

              <div style={{ fontSize:14, fontWeight:600, color:T.text, marginBottom:6, lineHeight:1.4 }}>{g.title}</div>

              {/* Assigned to */}
              {g.assigned_to_name && (
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                  <div style={{ width:22, height:22, borderRadius:"50%", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:"#fff", flexShrink:0 }}>
                    {g.assigned_to_name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                  </div>
                  <span style={{ fontSize:12, color:T.muted }}>{g.assigned_to_name}</span>
                  {g.assigned_to_dept && <span style={{ fontSize:11, color:T.dim }}>· {g.assigned_to_dept}</span>}
                </div>
              )}

              {/* Progress */}
              <div style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:4 }}>
                  <span style={{ color:T.muted }}>Progress</span>
                  <span style={{ color:T.cyan, fontFamily:"JetBrains Mono,monospace", fontWeight:700 }}>{g.progress}%</span>
                </div>
                <div style={{ height:6, background:T.toggle_bg, borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${g.progress}%`,
                    background: g.status==="Completed"?`linear-gradient(90deg,${T.green},${T.cyan})`:
                                g.status==="Submitted"?`linear-gradient(90deg,${T.amber},${T.red})`:
                                `linear-gradient(90deg,${T.cyan},${T.purple})`,
                    borderRadius:3, transition:"width 0.3s" }}/>
                </div>
              </div>

              <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${P_COLOR[g.priority]||T.amber}15`, color:P_COLOR[g.priority]||T.amber }}>{g.priority}</span>
                <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${T.purple}1A`, color:T.purple }}>{g.category}</span>
                {g.due_date && <span style={{ fontSize:11, color:T.dim, marginLeft:"auto" }}>Due: {g.due_date}</span>}
              </div>
              {g.kpi_metric && <div style={{ marginTop:7, fontSize:11.5, color:T.muted }}><span style={{ color:T.dim }}>KPI:</span> {g.kpi_metric}</div>}
              {g.completion_note && (
                <div style={{ marginTop:8, padding:"8px 10px", background:`${T.amber}0D`, borderRadius:7, border:`1px solid ${T.amber}1A`, fontSize:12, color:T.amber }}>
                  <span style={{ color:T.dim }}>Employee note:</span> {g.completion_note}
                </div>
              )}
              {g.manager_note && (
                <div style={{ marginTop:6, padding:"8px 10px", background:`${T.green}0D`, borderRadius:7, border:`1px solid ${T.green}1A`, fontSize:12, color:T.green }}>
                  <span style={{ color:T.dim }}>Manager:</span> {g.manager_note}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn:"1/-1", textAlign:"center", padding:50, color:T.muted }}>
              <Target size={40} style={{ color:T.dim, marginBottom:12 }}/>
              <div style={{ fontSize:14 }}>{filter==="all"?"No goals yet — click New Goal":"No goals with this status"}</div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`, borderRadius:14, width:520, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>{editId?"Edit":"New"} Goal</h3>
              <button onClick={()=>setOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:22, display:"flex", flexDirection:"column", gap:12 }}>
              <div><label style={lbl}>Goal Title *</label><input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. Improve test coverage to 90%" style={inp(!form.title)}/></div>
              <div><label style={lbl}>Description</label><textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={2} style={{...inp(),resize:"vertical"}}/></div>
              <div><label style={lbl}>KPI Metric</label><input value={form.kpi_metric} onChange={e=>setForm({...form,kpi_metric:e.target.value})} placeholder="e.g. Test coverage %, certifications obtained" style={inp()}/></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Assign To</label>
                  <select value={form.assigned_to_id} onChange={e=>setForm({...form,assigned_to_id:e.target.value})} style={inp()}>
                    <option value="">Select employee</option>
                    {employees.map(e=><option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Category</label>
                  <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} style={inp()}>
                    {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Priority</label>
                  <select value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})} style={inp()}>
                    {PRIORITIES.map(p=><option key={p}>{p}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Start Date</label><input type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})} style={inp()}/></div>
                <div><label style={lbl}>Due Date</label><input type="date" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})} style={inp()}/></div>
              </div>
              <div><label style={lbl}>Initial Progress: {form.progress}%</label>
                <input type="range" min={0} max={100} value={form.progress} onChange={e=>setForm({...form,progress:parseInt(e.target.value)})} style={{ width:"100%", accentColor:T.cyan }}/>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setOpen(false)} className={styles.btnSecondary}>Cancel</button>
                <button onClick={save} className={styles.btnPrimary}>{editId?"Update":"Create"} Goal</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manager Review Modal */}
      {reviewOpen && reviewGoal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setReviewOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.green}33`, borderRadius:14, width:460, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>Review Goal Completion</h3>
              <button onClick={()=>setReviewOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:22, display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ background:T.surface_soft, borderRadius:9, padding:14 }}>
                <div style={{ fontSize:14, fontWeight:600, color:T.text, marginBottom:6 }}>{reviewGoal.title}</div>
                <div style={{ fontSize:12, color:T.muted }}>{reviewGoal.assigned_to_name} · {reviewGoal.category}</div>
                {reviewGoal.completion_note && (
                  <div style={{ marginTop:10, padding:"8px 12px", background:`${T.amber}0F`, borderRadius:8, fontSize:13, color:T.amber, border:`1px solid ${T.amber}1F` }}>
                    <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Employee's completion note</div>
                    {reviewGoal.completion_note}
                  </div>
                )}
              </div>
              <div>
                <label style={lbl}>Decision</label>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <button onClick={()=>setReviewData({...reviewData,approved:true})}
                    style={{ padding:"10px", borderRadius:9, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600,
                      border:`1px solid ${reviewData.approved?`${T.green}66`:T.toggle_bg}`,
                      background:reviewData.approved?`${T.green}1A`:T.surface_faint,
                      color:reviewData.approved?T.green:T.muted }}>
                    <CheckCircle size={14} style={{ display:"inline", marginRight:6, verticalAlign:"middle" }}/>Approve & Complete
                  </button>
                  <button onClick={()=>setReviewData({...reviewData,approved:false})}
                    style={{ padding:"10px", borderRadius:9, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600,
                      border:`1px solid ${!reviewData.approved?`${T.red}66`:T.toggle_bg}`,
                      background:!reviewData.approved?`${T.red}1A`:T.surface_faint,
                      color:!reviewData.approved?T.red:T.muted }}>
                    <X size={14} style={{ display:"inline", marginRight:6, verticalAlign:"middle" }}/>Send Back
                  </button>
                </div>
              </div>
              <div><label style={lbl}>Manager Note</label>
                <textarea value={reviewData.manager_note} onChange={e=>setReviewData({...reviewData,manager_note:e.target.value})} rows={2}
                  placeholder="Feedback for the employee…" style={{...inp(),resize:"vertical"}}/></div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setReviewOpen(false)} className={styles.btnSecondary}>Cancel</button>
                <button onClick={submitReview} className={styles.btnPrimary}><Send size={13}/> Submit Review</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
